<div class="bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col w-[80vw] max-w-3xl mx-auto max-h-[90vh]">

  <!-- Header -->
  <div class="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between shrink-0">
    <h2 class="font-bold text-lg text-gray-800">Add Phase</h2>
    <button (click)="close.emit()"
      class="w-8 h-8 rounded-lg hover:bg-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>

  <!-- Form -->
  <form (ngSubmit)="onSubmit()" #projectForm="ngForm" class="flex flex-col flex-1 overflow-hidden">
    <div class="flex-1 overflow-y-auto p-8">
      <div class="flex flex-col gap-6">

        <!-- Phase Name -->
        <div class="flex flex-col gap-2">
          <label for="stageName" class="text-sm text-gray-700 font-semibold">Phase Name <span class="text-red-500">*</span></label>
          <input type="text" id="stageName" name="stageName" required [(ngModel)]="projectStageData.stageName"
            class="border border-gray-300 rounded-lg p-3.5 focus:ring-2 focus:ring-blue-500 outline-none placeholder-gray-400 transition-shadow text-sm"
            placeholder="e.g. Design, Development, Testing" autofocus />
        </div>

        <!-- Row: Color, Hours, Status -->
        <div class="grid grid-cols-3 gap-5">
          <div class="flex flex-col gap-2">
            <label for="color" class="text-sm text-gray-700 font-semibold">Color</label>
            <input type="color" id="color" name="color" required [(ngModel)]="projectStageData.color"
              class="w-full h-[46px] border border-gray-300 rounded-lg p-1 cursor-pointer bg-white" />
          </div>

          <div class="flex flex-col gap-2">
            <label for="estimatedTime" class="text-sm text-gray-700 font-semibold">Est. Hours</label>
            <input type="text" id="estimatedTime" inputmode="decimal" pattern="[0-9]*[.,]?[0-9]*"
              [(ngModel)]="estimatedTimeInput" name="estimatedTime"
              class="border border-gray-300 rounded-lg p-3.5 h-[46px] focus:ring-2 focus:ring-blue-500 outline-none bg-white transition-shadow text-sm"
              placeholder="e.g. 10.5" />
          </div>

          <div class="flex flex-col gap-2">
            <label for="status" class="text-sm text-gray-700 font-semibold">Status</label>
            <div class="relative h-[46px]">
              <select id="status" name="status" required [(ngModel)]="projectStageData.status"
                class="w-full h-full border border-gray-300 rounded-lg p-3 pr-10 focus:ring-2 focus:ring-blue-500 outline-none bg-white cursor-pointer transition-shadow appearance-none text-sm">
                @for(status of statusList; track status) {
                <option [value]="status">{{ getStatusText(status) }}</option>
                }
              </select>
              <div class="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-gray-400">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Row: Start Date, End Date -->
        <div class="grid grid-cols-2 gap-5">
          <div class="flex flex-col gap-2">
            <label class="text-sm text-gray-700 font-semibold">Start Date</label>
            <kendo-datepicker preventScrollPropagation [value]="startDateValue"
              (valueChange)="onStartDateChange($event)" [min]="minDate"
              [format]="'dd/MM/yyyy'" [navigation]="true"
              [popupSettings]="{ popupClass: 'kendo-datepicker-large' }"
              placeholder="Select start date" class="w-full h-[46px]">
            </kendo-datepicker>
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-sm text-gray-700 font-semibold">End Date</label>
            <kendo-datepicker preventScrollPropagation [value]="endDateValue"
              (valueChange)="onEndDateChange($event)" [min]="startDateValue"
              [format]="'dd/MM/yyyy'" [navigation]="true"
              [popupSettings]="{ popupClass: 'kendo-datepicker-large' }"
              placeholder="Select end date" class="w-full h-[46px]">
            </kendo-datepicker>
          </div>
        </div>

        <!-- Budget Check -->
        @if (projectBudget > 0) {
        @let remaining = getRemainingHours();
        @let over = isOverBudget();
        <div class="flex items-center gap-3 px-4 py-2.5 rounded-lg border"
          [class.bg-emerald-50]="!over" [class.border-emerald-200]="!over"
          [class.bg-red-50]="over" [class.border-red-200]="over">
          <div class="flex-1 flex items-center gap-3">
            <span class="text-sm font-semibold" [class.text-emerald-700]="!over" [class.text-red-600]="over">
              {{ existingPhaseHours + getParsedHours() | number:'1.0-1' }}h / {{ projectBudget }}h
            </span>
            <div class="flex-1 h-1.5 rounded-full max-w-[200px]" [class.bg-emerald-100]="!over" [class.bg-red-100]="over">
              <div class="h-1.5 rounded-full transition-all" [class.bg-emerald-400]="!over" [class.bg-red-400]="over"
                [style.width.%]="(existingPhaseHours + getParsedHours()) / projectBudget * 100 < 100 ? ((existingPhaseHours + getParsedHours()) / projectBudget * 100) : 100"></div>
            </div>
          </div>
          @if (over) {
          <span class="text-xs font-semibold text-red-500">⚠ +{{ (remaining * -1) | number:'1.0-1' }}h over budget</span>
          } @else {
          <span class="text-xs font-semibold text-emerald-600">{{ remaining | number:'1.0-1' }}h remaining</span>
          }
        </div>
        }

      </div>
    </div>

    <!-- Footer -->
    <div class="px-8 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between shrink-0">
      <button type="button" (click)="close.emit()"
        class="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition-colors cursor-pointer text-sm">
        Cancel
      </button>
      <button type="submit" [disabled]="!projectForm.form.valid || isSubmitting || isOverBudget()"
        class="px-6 py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer text-sm flex items-center gap-2">
        @if(isSubmitting) {
        <span class="animate-spin inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
        }
        {{ isSubmitting ? 'Creating...' : 'Create Phase' }}
      </button>
    </div>
  </form>
</div>
